/* Maharaja Super — infinite scroll for collection pages.
   The next page of the same section is fetched (?section_id=) and its cards are appended
   to #product-grid. Works again after filtering/sorting, because facets.js re-renders the
   grid (and this element) and connectedCallback runs again. */
if (!customElements.get('mx-infinite-scroll')) {
  class MxInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-mx-more]');
      this.status = this.querySelector('[data-mx-status]');
      this.loading = false;
      if (!this.dataset.nextUrl) return this.finish();

      if (this.button) {
        this.onClick = (event) => {
          event.preventDefault();
          this.load();
        };
        this.button.addEventListener('click', this.onClick);
      }

      if (this.dataset.auto !== 'false' && 'IntersectionObserver' in window) {
        this.classList.add('is-auto');
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.load();
          },
          { rootMargin: '0px 0px 900px 0px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
    }

    get grid() {
      const scope = this.closest('.collection') || document;
      return scope.querySelector('#product-grid');
    }

    async load() {
      const next = this.dataset.nextUrl;
      const grid = this.grid;
      if (this.loading || !next || !grid) return;
      this.loading = true;
      this.classList.add('is-loading');
      this.classList.remove('has-error');

      try {
        const url = new URL(next, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url.toString(), { credentials: 'same-origin' });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        const fragment = document.createDocumentFragment();
        doc.querySelectorAll('#product-grid > li').forEach((item) => {
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.classList.add('mx-infinite-new');
          fragment.appendChild(document.importNode(item, true));
        });
        grid.appendChild(fragment);

        const nextEl = doc.querySelector('mx-infinite-scroll');
        const nextUrl = nextEl ? nextEl.dataset.nextUrl : '';
        this.updateStatus(grid);

        if (nextUrl) {
          this.dataset.nextUrl = nextUrl;
          if (this.button) this.button.href = nextUrl;
        } else {
          this.dataset.nextUrl = '';
          this.finish();
        }
      } catch (error) {
        console.error('Infinite scroll:', error);
        this.classList.add('has-error');
        if (this.observer) this.observer.disconnect();
      } finally {
        this.loading = false;
        this.classList.remove('is-loading');
        // Re-check: if the loader is still on screen (short pages), load again.
        if (this.observer && this.dataset.nextUrl && !this.classList.contains('has-error')) {
          this.observer.unobserve(this);
          this.observer.observe(this);
        }
      }
    }

    updateStatus(grid) {
      const shown = grid.children.length;
      const total = Number(this.dataset.total) || shown;
      this.style.setProperty('--mx-progress', Math.min(100, (shown / total) * 100) + '%');
      if (!this.status) return;
      const template = this.dataset.statusText || 'Showing [shown] of [total] products';
      this.status.textContent = template.replace('[shown]', shown).replace('[total]', total);
    }

    finish() {
      if (this.observer) this.observer.disconnect();
      this.classList.add('is-done');
      if (this.status && this.dataset.endText) this.status.textContent = this.dataset.endText;
    }
  }

  customElements.define('mx-infinite-scroll', MxInfiniteScroll);
}
